// src/utils/intentionFte.ts
//
// Single source of truth for "what FTE did this person actually ask for?".
//
// An intention stores FTE in up to three places, and they do not always agree:
//
//   intentions.availability.fte        number  — the submission form's field
//   intentions.availability.ftePreference  string — same value, as typed
//   intentions.targetFTE               number  — the older/imported field
//   intentions.currentFTE              number  — what they are on NOW, not a request
//
// `currentFTE` is deliberately last: it is the present state, not a request, and
// falling back to it silently turns "no answer" into "no change". It is kept only
// because TeacherAssignmentModal already reads it and dropping it would change
// that screen's behaviour.
//
// Precedence matches TeacherAssignmentModal.tsx, which had the only correct
// reading of this in the codebase.
//
// See issue #226: nothing ever wrote this back to pit.staff.fte, so 47 of the 193
// staff with a 2027 intention were being measured against the wrong capacity.

export interface IntentionLike {
  targetFTE?: number | null;
  currentFTE?: number | null;
  availability?: { fte?: number | null; ftePreference?: string | null } | null;
}

/** The FTE this person requested, or null when they gave no usable answer. */
export function resolveIntendedFte(intention: IntentionLike | null | undefined): number | null {
  if (!intention) return null;

  // "I'd like to discuss my options" is NOT a request for any particular FTE.
  // The form nonetheless stores numericFte: 1.0 alongside it, so reading the
  // number at face value turns 19 staff into apparent requests for full time —
  // and offered to apply that to people like Claudio Parissi, who is on 0.9 and
  // asked to talk precisely because 0.9 was not on the form.
  //
  // Returning null keeps them out of the apply list entirely; they belong in the
  // discuss queue, which is where they asked to be.
  if (intention.availability?.ftePreference === 'discuss') return null;

  const candidates: unknown[] = [
    intention.availability?.fte,
    intention.availability?.ftePreference,
    intention.targetFTE,
    intention.currentFTE,
  ];

  for (const raw of candidates) {
    if (raw === null || raw === undefined || raw === '') continue;
    const n = typeof raw === 'number' ? raw : Number(raw);
    // An FTE outside (0, 1] is a data-entry error, not a request. Skip rather
    // than write a nonsense capacity onto the staff record.
    if (Number.isFinite(n) && n > 0 && n <= 1) return n;
  }

  return null;
}

/** Round to the 2dp the staff record stores, so 0.6 !== 0.6000000000000001. */
export function sameFte(a: number | null | undefined, b: number | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return Math.round(a * 100) === Math.round(b * 100);
}
